import React from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { MonthlyChartPoint } from '../../../utils/projectDetailChartData'
import { DETAIL_PIE_COLORS } from '../../../utils/projectDetailChartData'
import { detailRechartsTheme } from './project-detail-recharts-theme'

export interface ProjectDetailMonthlyHoursTrendChartProps {
  isDark: boolean
  monthlyChartData: MonthlyChartPoint[]
  /** Default matches Trends tab */
  height?: number
}

/**
 * Composed monthly hours chart: billable / non-billable / leave (h) bars
 * (leave: 1 day = 8 h) + utilization line.
 * Monthly breakdown tab — chart view.
 */
export function ProjectDetailMonthlyHoursTrendChart({
  isDark,
  monthlyChartData,
  height = 300,
}: ProjectDetailMonthlyHoursTrendChartProps) {
  const { gridStroke, tick, tooltipBg, tooltipBorder } =
    detailRechartsTheme(isDark)

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={monthlyChartData}
        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={gridStroke}
          opacity={0.8}
        />
        <XAxis
          dataKey="name"
          stroke={tick}
          tick={{ fontSize: 11 }}
          angle={-45}
          textAnchor="end"
          height={56}
        />
        <YAxis
          yAxisId="left"
          stroke={tick}
          tick={{ fontSize: 11 }}
          label={{
            value: 'Hours',
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 11 },
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke={tick}
          tick={{ fontSize: 11 }}
          label={{
            value: 'Util %',
            angle: 90,
            position: 'insideRight',
            style: { fontSize: 11 },
          }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: tooltipBg,
            border: `1px solid ${tooltipBorder}`,
            borderRadius: 8,
            fontSize: 12,
          }}
          formatter={(value: number, name: string) => {
            if (name === 'Leave (h)' && typeof value === 'number')
              return [
                `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} h (${(value / 8).toLocaleString(undefined, { maximumFractionDigits: 2 })} d)`,
                name,
              ]
            if (name === 'Utilization %' && typeof value === 'number')
              return [`${value.toFixed(1)}%`, name]
            return [value, name] as [number | string, string]
          }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar
          yAxisId="left"
          dataKey="billableHours"
          name="Billable"
          fill={DETAIL_PIE_COLORS.billable}
          stroke="#0f766e"
          strokeWidth={1}
          barSize={20}
          radius={[2, 2, 0, 0]}
        />
        <Bar
          yAxisId="left"
          dataKey="nonBillableHours"
          name="Non-Billable"
          fill={DETAIL_PIE_COLORS.nonBillable}
          stroke="#92400e"
          strokeWidth={1}
          barSize={20}
          radius={[2, 2, 0, 0]}
        />
        <Bar
          yAxisId="left"
          dataKey="leaveHours"
          name="Leave (h)"
          fill={DETAIL_PIE_COLORS.leave}
          stroke="#0369a1"
          strokeWidth={1}
          barSize={20}
          radius={[2, 2, 0, 0]}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="utilizationRate"
          name="Utilization %"
          stroke="#64748b"
          strokeWidth={2}
          dot={{ fill: '#64748b', r: 4 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
